import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Check, 
  ArrowLeft,
  Loader2,
  Bell,
  Star,
  Shield,
  Zap
} from 'lucide-react';

import { apiClient, formatP0Error } from '../lib/api';
import '@/styles/ios26-design-system.css';

export default function PricingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/pricing/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      // Fallback plans
      setPlans({
        starter: {
          name: 'Starter',
          price: 19,
          currency: 'eur',
          features: [
            '1 site connecté',
            '1 moteur IA',
            "Jusqu'à 5 plateformes",
            'Analyse IA automatique',
            '1-2 contenus / jour',
            'Mode automatique ON/OFF',
            'Validation manuelle',
            'Support email'
          ],
          available: true
        },
        pro: {
          name: 'Pro',
          price: 49,
          currency: 'eur',
          features: [
            'Multi-sites',
            'Plateformes illimitées',
            'Publication automatique',
            '2+ contenus / jour',
            'Statistiques avancées',
            'Support prioritaire'
          ],
          available: false,
          coming_soon: true
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStarterCheckout = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setCheckoutLoading(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/payments/checkout', {
        plan_tier: 'starter',
        email: email,
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Erreur lors de la création du paiement');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(formatP0Error(error) || 'Erreur lors du paiement');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleNotifyPro = async () => {
    if (!notifyEmail || !notifyEmail.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setNotifyLoading(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.post('/notify/pro-plan', { email: notifyEmail });
      toast.success('Vous serez notifié dès que le plan Pro sera disponible !');
      setNotifyEmail('');
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de l\'inscription');
    } finally {
      setNotifyLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-semantic">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'hsl(var(--color-primary))' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen ios-bg-gradient">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <nav className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80">
            <Sparkles className="w-8 h-8" style={{ color: 'hsl(var(--color-primary))' }} />
            <span className="text-xl font-bold text-semantic">AI Marketing Engine</span>
          </button>
          <Button variant="ghost" onClick={() => navigate('/auth')} className="text-semantic-2 hover:text-semantic">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Connexion
          </Button>
        </nav>
      </header>

      {/* Pricing Section */}
      <section className="container mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-12">
          <Badge className="mb-4" style={{ backgroundColor: 'hsl(var(--color-info-bg))', color: 'hsl(var(--color-info-text))' }}>
            <Zap className="w-3 h-3 mr-1" /> Tarification simple
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-semantic">
            Choisissez votre plan
          </h1>
          <p className="text-lg max-w-2xl mx-auto text-semantic-2">
            Un abonnement est requis pour activer le moteur. 
            Sans engagement, annulable à tout moment.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Starter Plan */}
          <Card 
            className="relative shadow-lg"
            style={{
              backgroundColor: 'hsl(var(--pricing-card-bg))',
              borderWidth: '2px',
              borderColor: 'hsl(var(--color-primary))'
            }}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge style={{ backgroundColor: 'hsl(var(--color-primary))', color: 'white' }}>
                <Star className="w-3 h-3 mr-1" /> Recommandé
              </Badge>
            </div>
            <CardHeader className="text-center pt-8">
              <CardTitle className="text-2xl" style={{ color: 'hsl(var(--pricing-title-text))' }}>{plans.starter?.name}</CardTitle>
              <CardDescription className="text-semantic-2">Pour démarrer votre marketing automatique</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold" style={{ color: 'hsl(var(--pricing-price-text))' }}>{plans.starter?.price}€</span>
                <span className="text-semantic-muted">/mois</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                {plans.starter?.features?.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3" style={{ color: 'hsl(var(--pricing-feature-text))' }}>
                    <Check className="w-5 h-5 flex-shrink-0" style={{ color: 'hsl(var(--color-success))' }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-3 pt-4 border-t" style={{ borderColor: 'hsl(var(--color-border))' }}>
                <Label htmlFor="email" className="text-semantic">Votre email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-semantic"
                />
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={handleStarterCheckout}
                  disabled={checkoutLoading}
                  style={{
                    backgroundColor: 'hsl(var(--pricing-button-primary-bg))',
                    color: 'hsl(var(--pricing-button-primary-text))'
                  }}
                >
                  {checkoutLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirection...</>
                  ) : (
                    'Commencer maintenant'
                  )}
                </Button>
                <p className="text-xs text-center text-semantic-muted">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Paiement sécurisé par Stripe
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card 
            className="relative opacity-90"
            style={{
              backgroundColor: 'hsl(var(--pricing-card-bg))',
              borderWidth: '2px',
              borderColor: 'hsl(var(--color-border))'
            }}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge variant="secondary" className="bg-semantic-surface-2 text-semantic-2">
                Bientôt disponible
              </Badge>
            </div>
            <CardHeader className="text-center pt-8">
              <CardTitle className="text-2xl" style={{ color: 'hsl(var(--pricing-title-text))' }}>{plans.pro?.name}</CardTitle>
              <CardDescription className="text-semantic-2">Pour les professionnels exigeants</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold" style={{ color: 'hsl(var(--pricing-price-text))' }}>{plans.pro?.price}€</span>
                <span className="text-semantic-muted">/mois</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                {plans.pro?.features?.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-semantic-muted">
                    <Check className="w-5 h-5 flex-shrink-0 opacity-50" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-3 pt-4 border-t" style={{ borderColor: 'hsl(var(--color-border))' }}>
                <Label htmlFor="notify-email" className="text-semantic">Être notifié du lancement</Label>
                <Input
                  id="notify-email"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="input-semantic"
                />
                <Button 
                  variant="outline" 
                  className="w-full" 
                  size="lg"
                  onClick={handleNotifyPro}
                  disabled={notifyLoading}
                  style={{
                    backgroundColor: 'hsl(var(--pricing-button-secondary-bg))',
                    color: 'hsl(var(--pricing-button-secondary-text))',
                    borderColor: 'hsl(var(--color-border))'
                  }}
                >
                  {notifyLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Inscription...</>
                  ) : (
                    <><Bell className="w-4 h-4 mr-2" /> Être notifié</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ / Info */}
        <div className="mt-16 max-w-2xl mx-auto text-center">
          <h3 className="text-xl font-semibold mb-4 text-semantic">Questions fréquentes</h3>
          <div className="space-y-4 text-left">
            <div className="p-4 rounded-lg bg-semantic-surface-2">
              <h4 className="font-medium mb-2 text-semantic">Puis-je annuler à tout moment ?</h4>
              <p className="text-sm text-semantic-2">Oui, vous pouvez annuler votre abonnement à tout moment depuis votre tableau de bord. Aucun engagement.</p>
            </div>
            <div className="p-4 rounded-lg bg-semantic-surface-2">
              <h4 className="font-medium mb-2 text-semantic">Mes contenus sont-ils publiés automatiquement ?</h4>
              <p className="text-sm text-semantic-2">Le mode automatique est désactivé par défaut. Vous validez chaque contenu avant publication. Vous pouvez activer l&apos;automatisation quand vous êtes prêt.</p>
            </div>
            <div className="p-4 rounded-lg bg-semantic-surface-2">
              <h4 className="font-medium mb-2 text-semantic">Les contenus sont-ils générés par IA ?</h4>
              <p className="text-sm text-semantic-2">Oui, les textes et images sont créés par intelligence artificielle, optimisés pour votre audience et votre marque.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 mt-12 bg-semantic-surface-2">
        <div className="container mx-auto px-4 text-center text-sm text-semantic-2">
          <div className="flex justify-center gap-6 mb-4">
            <a href="/privacy-policy" className="hover:text-semantic transition-colors">Politique de confidentialité</a>
            <a href="/terms" className="hover:text-semantic transition-colors">CGU</a>
            <a href="mailto:contact@ai-marketing-engine.com" className="hover:text-semantic transition-colors">Contact</a>
          </div>
          <p>© 2025 AI Marketing Engine. Paiement sécurisé par Stripe.</p>
        </div>
      </footer>
    </div>
  );
}
